import { Controller } from "@hotwired/stimulus"

/**
 * Base Stimulus controller for Control Panel shared behavior.
 * Handles common CRUD operations (search, create, edit, delete) via fetch.
 */
export default class extends Controller {
  static targets = [
    "searchInput", "list",
    "createModal", "editModal",
    "editId", "deleteConfirmModal", "deleteId"
  ]

  connect() {
    this._showPendingFlash()
  }

  // ── Modal management ──────────────────────────────────────────────

  openCreateModal() {
    if (this.hasCreateModalTarget) {
      this.createModalTarget.classList.remove("hidden")
      this._lockScroll()
    }
  }

  closeCreateModal() {
    if (this.hasCreateModalTarget) {
      this.createModalTarget.classList.add("hidden")
      this._clearModalInputs(this.createModalTarget)
      this._unlockScroll()
    }
  }

  openEditModal(event) {
    if (!this.hasEditModalTarget) return
    const btn = event.currentTarget
    // Populate edit form from data-* attributes
    this.editModalTarget.querySelectorAll("[data-field]").forEach(input => {
      const field = input.dataset.field
      if (btn.dataset[field] !== undefined) {
        input.value = btn.dataset[field]
      }
    })
    if (this.hasEditIdTarget) {
      this.editIdTarget.value = btn.dataset.id
    }
    this.editModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  closeEditModal() {
    if (this.hasEditModalTarget) {
      this.editModalTarget.classList.add("hidden")
      this._clearModalInputs(this.editModalTarget)
      this._unlockScroll()
    }
  }

  openDeleteConfirm(event) {
    if (!this.hasDeleteConfirmModalTarget) return
    const btn = event.currentTarget
    if (this.hasDeleteIdTarget) {
      this.deleteIdTarget.value = btn.dataset.id
    }
    this.deleteConfirmModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  closeDeleteConfirm() {
    if (this.hasDeleteConfirmModalTarget) {
      this.deleteConfirmModalTarget.classList.add("hidden")
      this._unlockScroll()
    }
  }

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) {
      this._clearModalInputs(event.currentTarget)
      event.currentTarget.classList.add("hidden")
      this._unlockScroll()
    }
  }

  // ── Search (debounced) ────────────────────────────────────────────

  search() {
    clearTimeout(this._searchTimeout)
    this._searchTimeout = setTimeout(() => {
      this._performSearch()
    }, 300)
  }

  _performSearch() {
    // Subclasses should override or use data-action with a specific method
  }

  // ── CRUD helpers ──────────────────────────────────────────────────

  async submitCreate(url, body) {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeCreateModal()
        this._showToast(data.message || "Created successfully")
        this._reload()
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error creating item", "error")
      }
    } catch (e) {
      this._showToast("Network error", "error")
    }
  }

  async submitUpdate(url, body) {
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeEditModal()
        this._showToast(data.message || "Updated successfully")
        this._reload()
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error updating item", "error")
      }
    } catch (e) {
      this._showToast("Network error", "error")
    }
  }

  async submitDelete(url) {
    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeDeleteConfirm()
        this._showToast(data.message || "Deleted successfully")
        this._reload()
      } else {
        this.closeDeleteConfirm()
        this._showToast(data.message || "Cannot delete item", "error")
      }
    } catch (e) {
      this._showToast("Network error", "error")
    }
  }

  // ── Utilities ─────────────────────────────────────────────────────

  _headers() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  _reload() {
    window.location.reload()
  }

  _reloadWithToast(message, type = "success") {
    sessionStorage.setItem("cpanel_flash", JSON.stringify({ message, type }))
    window.location.reload()
  }

  _showPendingFlash() {
    const raw = sessionStorage.getItem("cpanel_flash")
    if (!raw) return
    sessionStorage.removeItem("cpanel_flash")
    try {
      const { message, type } = JSON.parse(raw)
      this._showToast(message, type)
    } catch { /* ignore */ }
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  _lockScroll() {
    document.body.style.overflow = "hidden"
  }

  _unlockScroll() {
    document.body.style.overflow = ""
  }

  _clearModalInputs(modal) {
    modal.querySelectorAll("input:not([type=hidden]), select, textarea").forEach(el => {
      if (el.type === "checkbox" || el.type === "radio") {
        el.checked = false
      } else {
        el.value = ""
      }
      el.classList.remove("border-red-500")
      if (!el.classList.contains("border-gray-300")) {
        el.classList.add("border-gray-300")
      }
    })
  }
}
